import React from 'react';
import { cn } from '@mentimeter/ragnar-tailwind-config';
import type { ButtonT } from './Button.types';
import { ERROR_STATE, SUCCESS_STATE } from './constants';
import { FelaButton } from './FelaButton';
import { ButtonContent } from './components';
import { buttonVariants } from './Button.variants';

export const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonT
>((props, ref) => {
  const {
    state,
    size,
    children,
    disabled,
    iconLeading,
    iconTrailing,
    variant,
    className,
    onClick,
    ...rest
  } = props;

  const hasState = Boolean(state);
  const isDisabled = disabled || hasState;
  const isLink = rest?.href !== undefined;
  const asLinkProp = rest?.as ?? 'a';
  const isButton = !isLink;

  const VariantMap =
    state === SUCCESS_STATE
      ? 'positive'
      : state === ERROR_STATE
        ? 'negative'
        : variant;

  const buttonVariantsClasses = buttonVariants({
    variant: VariantMap,
    size,
  });

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isDisabled) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  return (
    <FelaButton
      ref={ref}
      className={cn(buttonVariantsClasses, className)}
      data-has-state={hasState}
      onClick={handleClick}
      {...(isButton && { type: 'button', disabled: isDisabled })}
      {...(isLink && { as: asLinkProp, 'aria-disabled': isDisabled })}
      {...rest}
    >
      <ButtonContent
        state={state}
        size={size}
        children={children}
        iconLeading={iconLeading}
        iconTrailing={iconTrailing}
      />
    </FelaButton>
  );
});
